import { parseUTCFromAPI, parseUTCFromAPIToUnix } from '@/composable/useFormatDateAndHours'
import type { CustomSeriesRenderItem, EChartsOption } from 'echarts'
import * as echarts from 'echarts'
import { i18n } from '@/plugins/i18n'
import {
  translateRegisterChartSeriesName,
  type RegisterChartContinuousInput,
  type RegisterChartDoorLaneInput,
} from '@/utils/register-chart-series'
import {
  buildSensorStateSegments,
  DOOR_OPEN_STATE_OPEN,
} from './door-open-chart'
import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'

/** Leitura de canal no payload de registros (pode vir incompleta em metadados). */
type RegisterChartReading = { t?: string | number; v?: number }

export interface IRegisterChartSeriesPoint {
  x: Date
  y: number
}

export interface IRegisterChartSeries {
  name: string
  data: IRegisterChartSeriesPoint[]
  yAxisIndex?: number
  unitDisplay?: string
}

export interface IRegisterChartSensorData {
  sensor?: { id?: number; name?: string; unit_display?: string; sensor_type?: string }
  start_date?: string
  end_date?: string
  readings?: Record<string, Array<RegisterChartReading> | unknown>
}

export interface BuildRegisterChartSeriesOptions {
  namePrefix?: string
  yAxisIndex?: number
}

/** Monta séries de linha a partir do payload de readings (apenas canais com t/v). */
export function buildRegisterChartSeries(
  sensorData: IRegisterChartSensorData | null | undefined,
  options: BuildRegisterChartSeriesOptions = {},
): IRegisterChartSeries[] {
  if (!sensorData?.readings || typeof sensorData.readings !== 'object') {
    return []
  }

  const unitDisplay = sensorData.sensor?.unit_display ?? 'A'
  const { namePrefix, yAxisIndex } = options

  return Object.entries(sensorData.readings).flatMap(([channel, readings]) => {
    if (!Array.isArray(readings)) {
      return []
    }

    const data = (readings as RegisterChartReading[])
      // Ignora pontos sem timestamp/valor. Ex.: door_open retorna o canal
      // `time_door_open` com objetos {start_date, end_date, duration} (sem t/v).
      .filter((reading) => reading && reading.t != null && reading.v != null)
      .map((reading) => ({
        x: parseUTCFromAPI(reading.t as string | number),
        y: reading.v as number,
      }))

    // Descarta séries sem pontos válidos (canais de metadados como
    // `time_door_open`, `period_stats` ou `readings_stat`) para não poluir a legenda.
    if (data.length === 0) {
      return []
    }

    const channelLabel = translateRegisterChartSeriesName(channel)
    const name = namePrefix ? `${namePrefix} — ${channelLabel}` : channelLabel

    return [
      {
        name,
        data,
        ...(yAxisIndex != null ? { yAxisIndex } : {}),
        unitDisplay,
      },
    ]
  })
}

export function buildRegisterChartAxisRange(
  ...sensorDataList: Array<IRegisterChartSensorData | null | undefined>
): { min?: number; max?: number } {
  let min: number | undefined
  let max: number | undefined

  for (const data of sensorDataList) {
    if (data?.start_date == null || data?.end_date == null) continue
    const start = parseUTCFromAPIToUnix(data.start_date)
    const end = parseUTCFromAPIToUnix(data.end_date)
    if (start != null) {
      min = min == null ? start : Math.min(min, start)
    }
    if (end != null) {
      max = max == null ? end : Math.max(max, end)
    }
  }

  return { min, max }
}


export function useRegisterChart(sensorData: MaybeRefOrGetter<IRegisterChartSensorData | null | undefined>) {
  const unitDisplay = ref('A')

  const series = ref<IRegisterChartSeries[]>([])

  // Período do gráfico = período daquele sensor (não o período global da busca)
  const chartOptions = ref<object>({ xAxis: {}, unitDisplay: 'A' })

  const updateChartOptions = (data: IRegisterChartSensorData | null | undefined) => {
    if (data?.start_date == null || data?.end_date == null) return
    const { min, max } = buildRegisterChartAxisRange(data)
    chartOptions.value = {
      xAxis: { min, max },
      unitDisplay: unitDisplay.value,
    }
  }

  watch(
    () => [toValue(sensorData), i18n.global.locale] as const,
    ([newSensorData]) => {
      if (newSensorData) {
        unitDisplay.value = newSensorData.sensor?.unit_display ?? 'A'
        series.value = buildRegisterChartSeries(newSensorData)
        updateChartOptions(newSensorData)
      } else {
        series.value = []
      }
    },
    { immediate: true },
  )

  return { chartOptions, series }
}

/** Preenchimento das faixas de porta aberta no swimlane (grid inferior). */
export const REGISTER_CHART_DOOR_OPEN_COLOR = 'rgba(251, 84, 84, 0.85)'

/** Timeline de portas: preenchimento bem discreto (cinza) para períodos fechados. */
export const REGISTER_CHART_DOOR_CLOSED_COLOR = 'rgba(148, 163, 184, 0.22)'

/** Timeline de portas: vermelho translúcido preenchendo os períodos abertos. */
export const REGISTER_CHART_DOOR_OPEN_TRANSLUCENT = 'rgba(251, 84, 84, 0.35)'

/** Timeline de portas: borda mais fraca dos períodos abertos. */
export const REGISTER_CHART_DOOR_OPEN_BORDER = 'rgba(220, 38, 38, 0.7)'

/** Linha/a área opcional aplicada às séries contínuas do grid superior. */
export interface RegisterChartTopGridMark {
  markLineData?: any[]
  markAreaData?: any[]
}

export interface BuildRegisterChartOptionConfig {
  /** Séries elétricas (A / kW…) → yAxis esquerdo. */
  energySeries: RegisterChartContinuousInput[]
  /** Séries de temperatura (°C) → yAxis direito. */
  temperatureSeries: RegisterChartContinuousInput[]
  /** Swimlanes discretos de porta (grid inferior). */
  doors: RegisterChartDoorLaneInput[]
  /** Intervalo compartilhado (unix ms) dos dois eixos de tempo. */
  range: { min?: number; max?: number }
  energyUnit?: string
  temperatureUnit?: string
  /** Rótulo da legenda do status aberto (ex.: "Aberta"). Default: i18n tooltip.open. */
  doorOpenLegendLabel?: string
  /** Formata rótulos do eixo X visível. Default: HH:mm. */
  xAxisLabelFormatter?: (value: number) => string
  /** Formata o cabeçalho do tooltip. Default: dd/MM/yyyy HH:mm. */
  headerTimeFormatter?: (value: number) => string
  splitNumber?: number
  compact?: boolean
  expanded?: boolean
  /** Marks (análise/linhas) aplicados apenas às séries contínuas do grid superior. */
  topGridMark?: RegisterChartTopGridMark
}

function toContinuousData(
  series: RegisterChartContinuousInput,
): Array<[number, number]> {
  return (series.data ?? []).map((point) => [Number(point.x), Number(point.y)])
}

/** Duração compacta estilo badge (ex.: "1h 02min", "45s"). */
export function formatRegisterChartDuration(ms: number, withSeconds = true): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0s'
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (days > 0) return `${days}d ${hours}h ${minutes}min`
  if (hours > 0) return `${hours}h ${minutes}min`
  if (minutes > 0) return withSeconds ? `${minutes}min ${seconds}s` : `${minutes}min`
  return `${seconds}s`
}

/** Ponto amostrado mais próximo do timestamp (leitura corrente no instante do hover). */
export function sampleRegisterChartSeriesAt(
  series: RegisterChartContinuousInput,
  timestamp: number,
): { x: number; y: number } | null {
  const data = series.data ?? []
  if (data.length === 0) return null

  let low = 0
  let high = data.length - 1
  let nearest = data[0]
  let bestDist = Number.POSITIVE_INFINITY

  while (low <= high) {
    const mid = (low + high) >> 1
    const point = data[mid]
    const pointX = Number(point.x)
    const dist = Math.abs(pointX - timestamp)
    if (dist < bestDist) {
      bestDist = dist
      nearest = point
    }
    if (pointX === timestamp) break
    if (pointX < timestamp) low = mid + 1
    else high = mid - 1
  }

  return { x: Number(nearest.x), y: Number(nearest.y) }
}

/** Status de uma porta no instante `timestamp` — Aberta (com duração decorrida) ou Fechada. */
export function resolveRegisterChartDoorStatus(
  door: RegisterChartDoorLaneInput,
  timestamp: number,
): { open: boolean; elapsedMs?: number } | null {
  for (const interval of door.intervals ?? []) {
    if (timestamp >= interval.start && timestamp <= interval.end) {
      return { open: true, elapsedMs: timestamp - interval.start }
    }
  }
  return { open: false }
}

const DOOR_LANE_MIN_WIDTH = 2

/** Reserva lateral fixa (px) para alinhar a plot area do grid de portas com o superior. */
const REGISTER_CHART_TIMELINE_PLOT_GUTTER = 76
const REGISTER_CHART_TIMELINE_PLOT_GUTTER_COMPACT = 64

/** Fator da altura da banda em relação à faixa categórica da linha no timeline de portas. */
const REGISTER_CHART_TIMELINE_BAND_RATIO = 0.72

const renderRegisterChartDoorItem: CustomSeriesRenderItem = (params, api) => {
  const coordSys = params.coordSys as typeof params.coordSys & {
    x: number
    y: number
    width: number
    height: number
  }
  const categoryIndex = api.value(0)
  const start = api.coord([api.value(1), categoryIndex])
  const end = api.coord([api.value(2), categoryIndex])
  const height = (api.size([0, 1])[1] || 0) * REGISTER_CHART_TIMELINE_BAND_RATIO
  const width = Math.max(Math.abs(end[0] - start[0]), DOOR_LANE_MIN_WIDTH)

  const rectShape = echarts.graphic.clipRectByRect(
    {
      x: Math.min(start[0], end[0]),
      y: start[1] - height / 2,
      width,
      height,
    },
    {
      x: coordSys.x,
      y: coordSys.y,
      width: coordSys.width,
      height: coordSys.height,
    },
  )
  if (!rectShape) return undefined

  return {
    type: 'rect',
    transition: ['shape'],
    shape: {
      ...rectShape,
      r: 0,
    },
    style: api.style(),
  }
}

/** Constroi o option ECharts completo do gráfico multi-sensor (grid superior + swimlanes de porta). */
export function buildChartOption(config: BuildRegisterChartOptionConfig): EChartsOption {
  const {
    energySeries,
    temperatureSeries,
    doors,
    range,
    energyUnit = energySeries[0]?.unit || 'A',
    temperatureUnit = temperatureSeries[0]?.unit || '°C',
    doorOpenLegendLabel = String(i18n.global.t('registers.chart.tooltip.open')),
    xAxisLabelFormatter = (value: number) =>
      new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    headerTimeFormatter = (value: number) =>
      new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    splitNumber,
    compact = false,
    expanded = false,
    topGridMark,
  } = config

  const hasDoors = doors.length > 0
  const continuousAggregated = [...energySeries, ...temperatureSeries]
  const legendNames = continuousAggregated.map((s) => s.name)

  const doorSeriesData = doors.flatMap((door, index) => {
    const segments = buildSensorStateSegments(
      (door.intervals ?? []).map((interval) => ({ y: [interval.start, interval.end] })),
      (value) => Number(value),
      range.min,
      range.max,
    )
    return segments.map((segment) => ({
      value: [index, segment.start, segment.end],
      name: segment.state,
      itemStyle:
        segment.state === DOOR_OPEN_STATE_OPEN
          ? {
              color: REGISTER_CHART_DOOR_OPEN_TRANSLUCENT,
              borderColor: REGISTER_CHART_DOOR_OPEN_BORDER,
              borderWidth: 1,
            }
          : {
              color: REGISTER_CHART_DOOR_CLOSED_COLOR,
            },
    }))
  })

  const buildContinuousSeries = (series: RegisterChartContinuousInput[], yAxisIndex: number) =>
    series.map((s) => ({
      name: s.name,
      type: 'line' as const,
      smooth: false,
      symbol: 'none',
      lineStyle: { width: 2 },
      yAxisIndex,
      z: 2,
      data: toContinuousData(s),
      markArea: topGridMark?.markAreaData?.length
        ? { silent: true, z: 0, data: topGridMark.markAreaData }
        : undefined,
      markLine: topGridMark?.markLineData?.length
        ? { symbol: 'none', data: topGridMark.markLineData }
        : undefined,
    }))

  const baseGrid = {
    left: '2%',
    right: '3%',
    containLabel: true,
  }

  const grid: EChartsOption['grid'] = hasDoors
    ? [
        {
          left: compact ? REGISTER_CHART_TIMELINE_PLOT_GUTTER_COMPACT : REGISTER_CHART_TIMELINE_PLOT_GUTTER,
          right: compact ? REGISTER_CHART_TIMELINE_PLOT_GUTTER_COMPACT : REGISTER_CHART_TIMELINE_PLOT_GUTTER,
          top: '9%',
          height: '52%',
        },
        {
          left: compact ? REGISTER_CHART_TIMELINE_PLOT_GUTTER_COMPACT : REGISTER_CHART_TIMELINE_PLOT_GUTTER,
          right: compact ? REGISTER_CHART_TIMELINE_PLOT_GUTTER_COMPACT : REGISTER_CHART_TIMELINE_PLOT_GUTTER,
          top: '64%',
          height: '24%',
        },
      ]
    : [{ ...baseGrid, top: '12%', height: '72%' }]

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      order: 'seriesDesc',
      formatter: (params: unknown) => {
        const dataPoints = Array.isArray(params) ? (params as any[]) : []
        const first = dataPoints[0]
        const axisValue = Number(first?.axisValue)
        const fallbackValue = Number(first?.value?.[0])
        const timestamp = Number.isFinite(axisValue)
          ? axisValue
          : Number.isFinite(fallbackValue) && fallbackValue > 1e11
            ? fallbackValue
            : Date.now()

        const lines: string[] = []

        const energyLines = energySeries
          .map((s) => {
            const sample = sampleRegisterChartSeriesAt(s, timestamp)
            return sample ? `${s.name}: <strong>${sample.y.toFixed(2)} ${s.unit || energyUnit}</strong>` : null
          })
          .filter((line): line is string => line != null)
        if (energyLines.length > 0) {
          lines.push(
            `<div style="margin-top:4px;font-weight:700;color:#334155">${i18n.global.t('registers.chart.tooltip.energy')}</div>`,
            ...energyLines,
          )
        }

        const temperatureLines = temperatureSeries
          .map((s) => {
            const sample = sampleRegisterChartSeriesAt(s, timestamp)
            return sample ? `${s.name}: <strong>${sample.y.toFixed(2)} ${s.unit || temperatureUnit}</strong>` : null
          })
          .filter((line): line is string => line != null)
        if (temperatureLines.length > 0) {
          lines.push(
            `<div style="margin-top:4px;font-weight:700;color:#334155">${i18n.global.t('registers.chart.tooltip.temperature')}</div>`,
            ...temperatureLines,
          )
        }

        if (hasDoors) {
          const doorLines = doors.map((door) => {
            const status = resolveRegisterChartDoorStatus(door, timestamp)
            if (!status || !status.open) {
              return `${door.name}: <strong>${i18n.global.t('registers.chart.tooltip.closed')}</strong>`
            }
            const duration = formatRegisterChartDuration(status.elapsedMs ?? 0)
            const openLabel = String(
              i18n.global.t('registers.chart.tooltip.open_for', { duration }),
            )
            return `${door.name}: <strong style="color:#DC2626">${i18n.global.t('registers.chart.tooltip.open')}</strong> <span style="color:#64748b">(${openLabel})</span>`
          })
          lines.push(
            `<div style="margin-top:4px;font-weight:700;color:#334155">${i18n.global.t('registers.chart.tooltip.doors')}</div>`,
            ...doorLines,
          )
        }

        return `<div style="font-weight:600;margin-bottom:4px">${headerTimeFormatter(timestamp)}</div>${lines.join('<br/>')}`
      },
    },
    legend: {
      data: hasDoors
        ? [
            { name: doorOpenLegendLabel, icon: 'rect', itemStyle: { color: REGISTER_CHART_DOOR_OPEN_COLOR } },
            ...legendNames,
          ]
        : legendNames,
      top: 0,
      textStyle: { fontSize: compact ? 10 : 12 },
    },
    grid,
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: [0, 1],
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        preventDefaultMouseMove: true,
        filterMode: 'none',
      },
      {
        type: 'slider',
        xAxisIndex: [0, 1],
        height: expanded ? 28 : compact ? 14 : 16,
        bottom: expanded ? '2%' : compact ? 2 : 4,
        borderColor: 'transparent',
        fillerColor: 'rgba(99, 102, 241, 0.2)',
        handleStyle: { color: '#6366f1' },
        dataBackground: { lineStyle: { opacity: 0.3 }, areaStyle: { opacity: 0.05 } },
        moveHandleSize: expanded ? 10 : 6,
        textStyle: { fontSize: expanded ? 11 : compact ? 9 : 10 },
        filterMode: 'none',
      },
    ],
    axisPointer: {
      link: [{ xAxisIndex: 'all' }],
    },
    xAxis: hasDoors
      ? [
          {
            type: 'time',
            gridIndex: 0,
            min: range.min,
            max: range.max,
            boundaryGap: [0, 0],
            show: false,
            axisLabel: { show: false },
          },
          {
            type: 'time',
            gridIndex: 1,
            min: range.min,
            max: range.max,
            boundaryGap: [0, 0],
            splitNumber,
            axisLabel: {
              fontSize: compact ? 9 : 10,
              hideOverlap: true,
              showMinLabel: true,
              showMaxLabel: true,
              margin: compact ? 10 : 8,
              formatter: xAxisLabelFormatter,
            },
          },
        ]
      : {
          type: 'time',
          min: range.min,
          max: range.max,
          boundaryGap: [0, 0],
          splitNumber,
          axisLabel: {
            fontSize: compact ? 9 : 10,
            hideOverlap: true,
            showMinLabel: true,
            showMaxLabel: true,
            formatter: xAxisLabelFormatter,
          },
        },
    yAxis: hasDoors
      ? [
          {
            type: 'value',
            gridIndex: 0,
            name: energyUnit,
            nameLocation: 'end',
            nameGap: 8,
            axisLabel: {
              fontSize: 10,
              formatter: (value: number) => `${value?.toFixed(2)} ${energyUnit}`,
            },
            splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
          },
          {
            type: 'value',
            gridIndex: 0,
            name: temperatureUnit,
            nameLocation: 'end',
            nameGap: 8,
            position: 'right',
            axisLabel: {
              fontSize: 10,
              formatter: (value: number) => `${value?.toFixed(2)} ${temperatureUnit}`,
            },
            splitLine: { show: false },
          },
          {
            type: 'category',
            gridIndex: 1,
            data: doors.map((door) => door.name),
            axisLabel: {
              fontSize: compact ? 10 : 11,
              width:
                (compact ? REGISTER_CHART_TIMELINE_PLOT_GUTTER_COMPACT : REGISTER_CHART_TIMELINE_PLOT_GUTTER) - 12,
              overflow: 'truncate',
            },
            splitLine: { show: false },
            axisTick: { show: false },
          },
        ]
      : [
          {
            type: 'value',
            name: energyUnit,
            nameLocation: 'end',
            nameGap: 8,
            axisLabel: {
              fontSize: 10,
              formatter: (value: number) => `${value?.toFixed(2)} ${energyUnit}`,
            },
            splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
          },
        ],
    series: [
      ...buildContinuousSeries(energySeries, 0),
      ...buildContinuousSeries(temperatureSeries, 1),
      ...(hasDoors
        ? [
            {
              name: doorOpenLegendLabel,
              type: 'custom' as const,
              xAxisIndex: 1,
              yAxisIndex: 2,
              renderItem: renderRegisterChartDoorItem,
              encode: { x: [1, 2], y: 0 },
              data: doorSeriesData,
              clip: false,
              z: 0,
            },
          ]
        : []),
    ],
  }
}
